import {Template3D} from 'components/customer/RoomPlanner/types';

/**
 * Consumers expect variables to be a JSON object with a `fields` array, so
 * normalise missing, empty, double-encoded or field-less variables.
 * @param {string | null} raw - Raw variables attribute from the Batch tag.
 * @return {string} - JSON string that always contains a `fields` array.
 */
const normalizeVariables = (raw: string | null): string => {
    let parsed: unknown = {};

    try {
        parsed = raw ? JSON.parse(raw) : {};
        if (typeof parsed === 'string') parsed = JSON.parse(parsed);
    } catch {
        parsed = {};
    }

    const variables =
        parsed && typeof parsed === 'object' && !Array.isArray(parsed)
            ? (parsed as Record<string, unknown>)
            : {};

    return JSON.stringify({
        ...variables,
        fields: Array.isArray(variables.fields) ? variables.fields : [],
    });
};

/**
 * Maps the /cabinet/<productId>/preview-template XML response into the
 * template_3d tuple shape the 3D preview and room planner consume.
 * The template name and variables live as attributes on the root Batch tag.
 * @param {string} xml - Raw XML returned by the preview-template endpoint.
 * @param {number} productId - Product (cabinet type) id the template belongs to.
 * @return {Template3D | null} - Mapped template, or null if the XML is invalid.
 */
export const mapPreviewTemplate = (
    xml: string,
    productId: string
): Template3D | null => {
    if (!xml) return null;

    const xmlDoc = new DOMParser().parseFromString(xml, 'text/xml');
    const batch = xmlDoc.documentElement;

    if (
        !batch ||
        batch.nodeName !== 'Batch' ||
        xmlDoc.getElementsByTagName('parsererror').length > 0
    ) {
        return null;
    }

    return [
        {
            attributes: {
                template: batch.getAttribute('template') ?? '',
                variables: normalizeVariables(batch.getAttribute('variables')),
                cabinet_id: Number(productId),
            },
        },
        xml,
    ];
};
